import { camelCase, addEvents } from "./../utils";

export default class {
  constructor({ trigger, content, container, relations, scrollTest, offsetElements, externalEvents, eventString, timing, props }) {
    this.#trigger = trigger;
    this.#content = content;

    container ? (this.#container = container) : false;
    relations ? (this.#relations = relations) : false;

    scrollTest ? (this.#scrollTest = scrollTest) : false;
    this.#offsetElements = offsetElements
      ? typeof offsetElements === "string"
        ? [...document.querySelectorAll(offsetElements)]
        : typeof offsetElements[Symbol.iterator] === "function"
        ? [...offsetElements]
        : [offsetElements]
      : [];

    externalEvents ? (this.#externalEvents = externalEvents) : false;
    !!eventString ? (this.#eventString = eventString) : false;

    timing ? (this.#timing = timing) : false;
    props
      ? ((this.#properties.props = props.map(camelCase)), (this.#properties.default = false))
      : false;

    this.#initialize();
    return this;
  }

  #trigger; // required element to click on that toggles the active state
  #content; // required element whose styles are animated

  #container; // optional element that also gets the active flag
  #relations = []; // array of toggle instances that close when another one opens

  #scrollTest = () => false; // function (or bool) used to determine if we should scroll to the content on toggle
  #offsetElements = []; // array of elements whose vertical height should be accounted for if scrollTest = true

  #externalEvents = false //set to true to handle event triggering externally
  #eventString = "click.stop.prevent enter.stop.prevent"; // event string to pass to addEvents 

  #properties = { //properties to toggle (default is height from 0 to auto)
    default: true,
    props: ["height"]
  };
  #timing = {//animation timing properties
    duration: 350,
    easing: "ease"
  };


  #isActive = false; // active state of the toggle
  #id = (() =>  //unique data id applied to the trigger and content for scoping css and for relating labels to content
    `data-${
      Math.random()
      .toString(36)
      .split("")
      .filter((value, index, self) => self.indexOf(value) === index)
      .join("")
      .substr(2, 8)}`
  )(); 

  #make_styles() {
    const style = document.createElement("style");
    style.textContent = `
			[${this.#id}] {
				overflow: hidden;
				height: 0;
        visibility: hidden;
        opacity: 0;
        transition-property: visibility opacity;
        transition-duration: ${this.#timing.duration}ms;
        transition-timing-function: ${this.#timing.easing};
			}
			[${this.#id}][data-active] {
				height: auto;
        opacity: 1;
        visibility: visible;
			}`;
    document.head.insertAdjacentElement("beforeend", style);
  }
  #process_trigger() {
    const t = this.#trigger;
    const t_id = `${this.#id}-trigger`;
    const c_id = `${this.#id}-content`;
    t.setAttribute("id", t_id);
    t.setAttribute("aria-expanded", "false");
    t.setAttribute("aria-labelledby", t_id);
    t.setAttribute("aria-controls", c_id);
    t.setAttribute("tabindex", "0");
    /*t.setAttribute("role", "button");*/
    if (!this.#externalEvents) {
      if (this.#eventString.includes('hover')) {
        addEvents(t, this.#eventString, {
          in: this.toggle,
          out: this.toggle
        });
      }
      else {
        addEvents(t, this.#eventString, this.toggle);
      }
    }
  }
  #process_content() {
    const c = this.#content;
    const t_id = `${this.#id}-trigger`;
    const c_id = `${this.#id}-content`;
    c.setAttribute(this.#id, "");
    c.setAttribute("id", c_id);
    c.setAttribute("aria-labelledby", t_id);
    c.setAttribute("role", "region");
  }
  #initialize() {
    this.#properties.default ? this.#make_styles() : false;
    this.#process_trigger();
    this.#process_content();
  }
  #get_property_values(c) {
    const styles = getComputedStyle(c);
    return this.#properties.props.reduce((values, prop) => {
      values[prop] = styles[prop];
      return values;
    }, {});
  }
  #get_offset() {
    return this.#offsetElements.reduce(
      (offset, o) => ((offset += o.offsetHeight), offset),
      0
    );
  }
  #animate(el, keyframes) {
    return el.animate(keyframes, this.#timing);
  }
  #scroll(t) {
    const top = t.getBoundingClientRect().top - this.#get_offset();
    scrollBy({ top, behavior: "smooth" });
  }
  //Public Methods
  toggle = async () => {
    const container = this.#container;
    const trigger = this.#trigger;
    const content = this.#content;
    const keyframes = [];
    const do_toggle = () => {
      this.#isActive = !this.#isActive;
      keyframes.push(this.#get_property_values(content));
      container ? container.toggleAttribute("data-active", this.#isActive) : false;
      trigger.toggleAttribute("data-active", this.#isActive);
      trigger.setAttribute("aria-expanded", this.#isActive);
      content.toggleAttribute("data-active", this.#isActive);
      keyframes.push(this.#get_property_values(content));
      this.#animate(content, keyframes);
      this.#scrollTest() ? this.#scroll(trigger) : false;
      return true;
    };

    if (this.#relations.length) {
      await this.#relations.reduce(async (prevPromise, relation) => {
        await prevPromise;
        if (relation === this) return Promise.resolve();
        else return relation.close();
      }, Promise.resolve());
    }

    do_toggle();
  };
  close() {
    return new Promise(resolve => {
      const container = this.#container;
      const trigger = this.#trigger;
      const content = this.#content;
      const keyframes = [];
      const do_toggle = () => {
        if (!this.#isActive) return (resolve(), true);
        this.#isActive = false;
        keyframes.push(this.#get_property_values(content));
        container ? container.removeAttribute("data-active") : false;
        trigger.removeAttribute("data-active");
        trigger.setAttribute("aria-expanded", false);
        content.removeAttribute("data-active");
        keyframes.push(this.#get_property_values(content));
        this.#animate(content, keyframes).onfinish = resolve;
        this.#scrollTest() ? this.#scroll(trigger) : false;
        return true;
      };

      do_toggle();
    });
  }
}
